import { useCallback, useEffect, useRef, useState } from "react";
import { Button, useTheme } from "@mui/material";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { Link } from "react-router-dom";
import { Portal } from "../Portals";
import useFocusTrap from "../../hooks/useFocusTrap";
import usePolling from "../../hooks/usePolling";
import useAIReport from "../../hooks/useAIReport";
import cn from "../../utils/cn";
import AIInterviewBadge from "./AIInterviewBadge";
import AIReportView from "./AIReportView";
import AIPlanView from "./AIPlanView";
import { QuestionsView, TranscriptView } from "./AIInterviewRecord";
import { liveDashboardPath } from "./AIInterviewStatusCell";
import { ErrorBox, LoadingLine, Section, WarningChips, chipWarnings, formatDateTime, formatDuration, humanize, isScore, toText } from "./aiShared";
import {
  aiSummaryOf,
  AI_MODE,
  AI_MODE_LABELS,
  AI_STATUS,
  COMPLETION_REASON_LABELS,
  fetchAIQuestions,
  fetchAISession,
  fetchAITranscript,
  getAIErrorMessage,
  hasAIInterviewStarted,
  isFinalAIStatus,
  shouldPollAISession,
} from "../../utils/aiInterviewApi";

const POLL_MS = 15000;

const StatusView = ({ session }) => {
  const theme = useTheme();
  const timings = session.timings || {};
  const progress = session.progress;
  const muted = { color: theme.palette.text.secondary || "#6b7280" };
  const facts = [
    ["Scheduled start", formatDateTime(timings.scheduled_start)],
    ["Bot joined", formatDateTime(timings.bot_joined_at)],
    ["Started", formatDateTime(timings.started_at)],
    ["Ended", formatDateTime(timings.ended_at)],
    ["Duration", formatDuration(timings.duration_seconds)],
    ["Planned length", session.config?.duration_minutes ? `${session.config.duration_minutes} min` : ""],
    ["Questions", session.plan_status === "ready" ? "Ready" : "Not generated yet"],
    ["Outcome", session.completion_reason ? COMPLETION_REASON_LABELS[session.completion_reason] || humanize(session.completion_reason) : ""],
  ].filter(([, value]) => value);

  return (
    <div className="flex flex-col gap-4">
      {session.status === AI_STATUS.FAILED && (
        <p className="p-3 rounded border border-red-300 bg-red-50 text-red-800 text-sm" role="alert">
          {session.failure?.message || "The AI interview failed."}
          {session.failure?.code ? <span className="block text-xs mt-1">Code: {session.failure.code}</span> : null}
        </p>
      )}
      {session.status === AI_STATUS.EVALUATING && (
        <p className="text-sm">The interview has ended. The report will be ready in about a minute.</p>
      )}
      {chipWarnings(session.warnings).length > 0 && (
        <Section title="Warnings">
          <WarningChips warnings={session.warnings} />
        </Section>
      )}
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
        {facts.map(([label, value]) => (
          <div key={label} className="flex justify-between gap-3 border-b py-1" style={{ borderColor: theme.palette.secondary.v4 }}>
            <dt style={muted}>{label}</dt>
            <dd className="text-right">{value}</dd>
          </div>
        ))}
      </dl>
      {progress?.sections?.length > 0 && (
        <Section title="Progress">
          <ul className="flex flex-col gap-1 text-sm">
            {progress.sections.map((section) => (
              <li key={section.name} className="flex justify-between gap-3">
                <span>{toText(section.name)}</span>
                <span style={muted}>
                  {humanize(toText(section.status))}
                  {isScore(section.used_minutes) ? ` · ${section.used_minutes.toFixed(0)}/${section.budget_minutes} min` : ` · ${section.budget_minutes} min`}
                </span>
              </li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
};

const ReportPending = ({ state, error, onRetry }) => {
  const theme = useTheme();
  if (state === "loading" || state === "idle") return <LoadingLine>Loading the report...</LoadingLine>;
  if (state === "preparing") return <LoadingLine>Report being prepared…</LoadingLine>;
  if (state === "error") return <ErrorBox onRetry={onRetry}>{error}</ErrorBox>;
  return (
    <div className="flex flex-wrap items-center gap-3 text-sm">
      <span>The report is taking longer than usual.</span>
      <Button
        onClick={onRetry}
        sx={{ backgroundColor: theme.palette.info.main, color: theme.palette.text.light, textTransform: "none" }}
      >
        Check again
      </Button>
    </div>
  );
};

// Status, question plan, report, questions and transcript for one interview's AI session
export default function AIInterviewDetailsModal({ isOpen, onClose, interviewId, title, subtitle, startTime, onSessionChange }) {
  const theme = useTheme();
  const modalRef = useFocusTrap(isOpen, onClose);
  const [session, setSession] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [segments, setSegments] = useState([]);
  const [transcriptComplete, setTranscriptComplete] = useState(false);
  const [tab, setTab] = useState("overview");
  const [highlightId, setHighlightId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const nextSeqRef = useRef(0);
  const onSessionChangeRef = useRef(onSessionChange);
  onSessionChangeRef.current = onSessionChange;

  const status = session?.status;
  const started = hasAIInterviewStarted(status);
  const mode = session?.config?.mode;
  const { report, setReport, state: reportState, error: reportError, reload: reloadReport } = useAIReport(
    interviewId,
    isOpen && status === AI_STATUS.COMPLETED
  );

  const loadSession = useCallback(async () => {
    const next = await fetchAISession(interviewId);
    setSession(next);
    onSessionChangeRef.current?.(interviewId, next);
    return next;
  }, [interviewId]);

  const loadQuestions = useCallback(async () => {
    setQuestions(await fetchAIQuestions(interviewId));
  }, [interviewId]);

  const loadTranscript = useCallback(async () => {
    const collected = [];
    let complete = false;
    // Keep paging until the server says the transcript is complete or no new segments arrive
    for (let page = 0; page < 20; page += 1) {
      const data = await fetchAITranscript(interviewId, nextSeqRef.current);
      const items = data?.segments || [];
      collected.push(...items);
      complete = Boolean(data?.complete);
      if (typeof data?.next_after_seq === "number") nextSeqRef.current = data.next_after_seq;
      if (complete || items.length === 0) break;
    }
    if (collected.length) {
      setSegments((previous) => {
        const seen = new Set(previous.map((segment) => segment.seq));
        return [...previous, ...collected.filter((segment) => !seen.has(segment.seq))].sort((a, b) => a.seq - b.seq);
      });
    }
    setTranscriptComplete(complete);
  }, [interviewId]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const next = await loadSession();
      if (next && hasAIInterviewStarted(next.status)) await loadQuestions();
    } catch (err) {
      setError(getAIErrorMessage(err, "Couldn't load the AI interview."));
    } finally {
      setLoading(false);
    }
  }, [loadSession, loadQuestions]);

  useEffect(() => {
    if (!isOpen || !interviewId) return;
    setSession(null);
    setQuestions([]);
    setSegments([]);
    setTranscriptComplete(false);
    nextSeqRef.current = 0;
    setTab("overview");
    setHighlightId("");
    loadAll();
  }, [isOpen, interviewId, loadAll]);

  // Poll while the interview is live (±1 h of the start), or while it's being scored
  const polling =
    isOpen &&
    Boolean(session) &&
    (shouldPollAISession(status, startTime || session.timings?.scheduled_start) || status === AI_STATUS.EVALUATING);

  usePolling(async () => {
    const next = await loadSession();
    if (!next) return;
    const tasks = [];
    if (hasAIInterviewStarted(next.status)) tasks.push(loadQuestions());
    if (tab === "transcript" && !transcriptComplete) tasks.push(loadTranscript());
    await Promise.all(tasks);
  }, POLL_MS, polling);

  useEffect(() => {
    if (!isOpen || tab !== "transcript" || transcriptComplete || !started) return;
    loadTranscript().catch((err) => setError(getAIErrorMessage(err, "Couldn't load the transcript.")));
    // Initial load when the tab opens; polling keeps it fresh
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, tab, started]);

  if (!isOpen) return null;

  const questionSeq = Object.fromEntries(questions.map((question) => [question.question_id, question.seq]));
  const openQuestion = (questionId) => {
    setTab("questions");
    setHighlightId(questionId);
  };

  const tabs = session
    ? [
      ["overview", report ? "Report" : "Status"],
      ["plan", mode === AI_MODE.PANELIST_WITH_AI ? "Question bank" : "Question plan"],
      ...(started ? [["questions", `Questions${questions.length ? ` (${questions.length})` : ""}`], ["transcript", "Transcript"]] : []),
    ]
    : [];
  const meta = [AI_MODE_LABELS[mode], session?.config?.round?.name].filter(Boolean).join(" · ");
  const showLiveLink = mode === AI_MODE.PANELIST_WITH_AI && status !== AI_STATUS.CANCELLED;

  return (
    <Portal>
      <div
        className="fixed inset-0 z-50 bg-black bg-opacity-70 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <div
          ref={modalRef}
          className="bg-white rounded-lg shadow-xl flex flex-col overflow-hidden w-full max-w-4xl"
          style={{ maxHeight: "calc(100dvh - 80px)", color: theme.palette.text.main }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="ai-interview-title"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="flex shrink-0 justify-between items-start gap-3 py-4 px-5 border-b" style={{ borderColor: theme.palette.secondary.v4 }}>
            <div className="flex flex-col gap-1 min-w-0">
              <h2 id="ai-interview-title" className="font-semibold text-lg">
                AI interview{title ? ` · ${title}` : ""}
              </h2>
              {subtitle && <p className="text-sm" style={{ color: theme.palette.text.secondary }}>{subtitle}</p>}
              {session && (
                <div className="flex flex-wrap items-center gap-2">
                  <AIInterviewBadge summary={aiSummaryOf(null, { ...session, overall_score: report?.overall_score })} />
                  {meta && <span className="text-xs" style={{ color: theme.palette.text.secondary }}>{meta}</span>}
                  {showLiveLink && (
                    <Link
                      to={liveDashboardPath(interviewId)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-medium text-blue-700 underline"
                    >
                      Open live dashboard
                    </Link>
                  )}
                </div>
              )}
              {session?.warnings?.length > 0 && <WarningChips warnings={session.warnings} />}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close AI interview"
              className="inline-flex shrink-0 items-center justify-center rounded-full bg-black text-white h-6 w-6 hover:bg-gray-800 transition-colors"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>

          {tabs.length > 1 && (
            <div className="flex shrink-0 gap-1 px-5 border-b overflow-x-auto" role="tablist" style={{ borderColor: theme.palette.secondary.v4 }}>
              {tabs.map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={tab === key}
                  onClick={() => setTab(key)}
                  className={cn(
                    "px-3 py-2 text-sm border-b-2 -mb-px whitespace-nowrap",
                    tab === key ? "border-blue-600 font-medium" : "border-transparent hover:border-gray-300"
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          <div className="flex flex-col gap-4 px-5 py-5 min-h-0 overflow-y-auto">
            {error && <ErrorBox onRetry={loadAll}>{error}</ErrorBox>}
            {loading ? (
              <LoadingLine>Loading AI interview...</LoadingLine>
            ) : !session ? (
              !error && <p className="text-sm">The AI interviewer isn't enabled for this interview.</p>
            ) : tab === "overview" ? (
              report ? (
                <AIReportView
                  report={report}
                  interviewId={interviewId}
                  questionSeq={questionSeq}
                  onOpenQuestion={openQuestion}
                  onReviewSaved={(review) => setReport((previous) => ({ ...previous, review }))}
                />
              ) : (
                <>
                  {status === AI_STATUS.COMPLETED && (
                    <ReportPending state={reportState} error={reportError} onRetry={reloadReport} />
                  )}
                  <StatusView session={session} />
                </>
              )
            ) : tab === "plan" ? (
              <AIPlanView
                interviewId={interviewId}
                status={status}
                onPlanChanged={() => loadSession().catch(() => {})}
              />
            ) : tab === "questions" ? (
              <QuestionsView questions={questions} highlightId={highlightId} />
            ) : (
              <>
                <TranscriptView segments={segments} />
                {!transcriptComplete && !isFinalAIStatus(status) && (
                  <p className="text-xs" style={{ color: theme.palette.text.secondary }}>Live: updates every few seconds.</p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </Portal>
  );
}
